import { GridColDef } from '@mui/x-data-grid';
import {
  Box,
  TextField,

  Alert,
  InputAdornment,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { atom, useSetAtom } from 'jotai';
import SearchIcon from '@mui/icons-material/Search';
import { useCallback, useMemo, useState, useEffect } from 'react';
import EditStoreOwnerModal from '../EditStoreOwnerModal';

import {
  useAllOffers,
  useDeleteOffer,
  useToggleOfferStatus,
} from '../../Query/OffersQuery';
import { usePermissions } from '../../../context/PermissionContext';
import React from 'react';
import { useSnackbar } from '../../Stateless/SnackBarProvider';
import { useManageCmsParams } from '../ManageCMS/useManageCmsParams';
import CommonDataGrid from '../../Stateless/CommonDataGrid';
import { useDebounce } from '../Utils/useDounce';
import PermissionActionButtons from '../../Stateless/Common/PermissionActionButtons';
import { usePermissionBasedColumns } from '../../Stateless/Common/ColumnUtils';
import EditOffers from './EditOffers';
import { COLORS } from '../../../theme';

export interface OfferDataType {
  id?: string;
  offerId?: string;
  couponCode?: string;
  couponName?: string;
  couponType?: string;
  couponValue?: number;
  maxCouponAmount?: number;
  couponStartDate?: string;
  validityDays?: number;
  termsAndConditions?: string;
  status?: string;
  offerType?: string;
  couponPerUserLimit?: number;
  totalCouponCount?: number;
  usedCouponCount?: number;
  expires?: string;
}

export const offerAtom = atom<OfferDataType | null>(null);

// type StoreWithSno = {
//   id: string;
//   sno: number;
//   name: string;
//   storeType: string;
//   choice: string;
//   price: number;
//   status: string;
// };


const ManageOffers = () => {
  const { showMessage } = useSnackbar();
  const { hasPermission } = usePermissions();
  const setOfferAtom = useSetAtom(offerAtom);
  const { getActionColumns } = usePermissionBasedColumns();
  // Change selectedRows to string[] for offerIds
  const [selectedRows, setSelectedRows] = useState<string[]>([]);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const snackbarMessage = ''; // Replace with actual message
  const snackbarSeverity: 'success' | 'error' | 'info' | 'warning' = 'info';
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [modalContent] = useState<'add' | 'edit'>('edit');

  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearch = useDebounce(searchTerm, 400);
  const { params, dispatch } = useManageCmsParams();
  const { data: offersData, isLoading, refetch } = useAllOffers(params);

  const { mutate: toggleOfferStatus } = useToggleOfferStatus();

  const { mutate: deleteOffer } = useDeleteOffer();


  //   const filteredRows = React.useMemo(() => {
  //     const storeOwners = choiceData?.data || [];

  //     // Add sno field to each row and ensure all required fields are present
  //     return storeOwners.map((row, index) => {
  //       // Ensure we have a valid row object
  //       if (!row) return { id: `empty-${index}`, sno: index + 1 } as StoreWithSno;

  //       // Create a new object with all the row properties plus sno
  //       const rowWithSno = {
  //         ...row,
  //         id: row.choiceId,
  //         sno: index + 1 + (Number(params.page - 1)) * Number(params.limit),
  //         name: row.storeName,
  //         storeType: row.storeType,
  //         choice: row.choiceNameEnglish,
  //         price: row.price,
  //         status: row.status,
  //       };

  //       return rowWithSno as StoreWithSno;
  //     });
  //   }, [params.search, choiceData, params.page, params.limit]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
  };

  useEffect(() => {
    dispatch({ type: 'SET_SEARCH', payload: debouncedSearch ? debouncedSearch : undefined });
  }, [debouncedSearch, dispatch]);

  // const handlePageChange = (newPage: number) => {
  //   dispatch({ type: 'SET_PAGE', payload: newPage + 1 });
  // };

  // const handlePageSizeChange = (newSize: number) => {
  //   dispatch({ type: 'SET_LIMIT', payload: newSize });
  // };

  const handleBlockClick = async () => {
    if (selectedRows.length === 0) {
      showMessage('Please select at least one offer to block', 'error');
    } else {
      const confirmed = window.confirm('Are you sure you want to block?');
      if (!confirmed) return;

      toggleOfferStatus({
        offerIds: selectedRows,
        status: 'blocked',
      });
    }
  };

  const handleUnBlockClick = async () => {
    if (selectedRows.length === 0) {
      showMessage('Please select at least one offer to unblock', 'error');
    } else {
      const confirmed = window.confirm('Are you sure you want to unblock?');
      if (!confirmed) return;

      toggleOfferStatus({
        offerIds: selectedRows,
        status: 'unblocked',
      });
    }
  };

  const handleDeleteClick = useCallback(() => {
    if (selectedRows.length === 0) {
      showMessage('Please select at least one offer to delete', 'error');
      return;
    }

    const confirmed = window.confirm('Are you sure you want to delete?');
    if (!confirmed) return;

    // Clear selected rows immediately after confirmation
    setSelectedRows([]);

    deleteOffer(selectedRows, {
      onSuccess: () => {
        showMessage('Offer deleted successfully', 'success');
        // Refetch data to update the table
        refetch();
      },
      onError: () => {
        showMessage('Error deleting offer', 'error');
      },
    });
  }, [selectedRows, showMessage, deleteOffer, refetch]);

  const handleBlockClickButton = async (id: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to block this Offer?'
    );
    if (!confirmed) return;

    toggleOfferStatus({
      offerIds: [id],
      status: 'blocked',
    });
  };
  const handleUnBlockClickButton = async (id: string) => {
    const confirmed = window.confirm(
      'Are you sure you want to unblock this Offer?'
    );
    if (!confirmed) return;

    toggleOfferStatus({
      offerIds: [id],
      status: 'unblocked',
    });
  };

  const handleDeleteButton = useCallback(
    (id: string) => {
      const confirmed = window.confirm('Are you sure you want to delete?');
      if (!confirmed) return;

      deleteOffer([id], {
        onSuccess: () => {
          showMessage('Offer deleted successfully', 'success');
          refetch();
        },
        onError: () => {
          showMessage('Error deleting offer', 'error');
        },
      });
    },
    [deleteOffer, showMessage, refetch]
  );

  const handleAddSubAdmin = () => {
    setOpenAddDialog(true);
  };

  const handleCloseAddDialog = () => {
    setOpenAddDialog(false);
    setOfferAtom(null); // Reset offer atom when closing add dialog
    refetch(); // Refetch offers list
  };

  const handleEditClick = (row: OfferDataType) => {
    setOfferAtom(row);
    setOpenEditDialog(true);
  };

  const handleCloseEditDialog = () => {
    setOpenEditDialog(false);
    setOfferAtom(null);
    refetch(); // Refetch offers list
  };

  // const handleCategorySelection = (selectionModel: GridRowId[]) => {
  //   // Convert all ids to numbers to match row id type
  //   const ids = selectionModel.map((id) => Number(id));
  //   setSelectedRows(ids);
  // };

  const RowActions: React.FC<{ row: any }> = ({ row }) => {
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };
    const handleClose = () => setAnchorEl(null);

    const id = typeof row.id === 'string' ? row.id : '';

    const canEdit = hasPermission('promotionalAndOffers', 'Edit');
    const canDelete = hasPermission('promotionalAndOffers', 'Delete');

    if (!canEdit && !canDelete) {
      return null;
    }

    return (
      <>
        <IconButton size="small" onClick={handleOpen}>
          <MoreVertIcon fontSize="small" />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {canEdit && (
            <MenuItem
              onClick={() => {
                handleClose();
                handleEditClick(row);
              }}
            >
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
          )}
          {canDelete && (
            <MenuItem
              onClick={() => {
                handleClose();
                if (id) {
                  handleDeleteButton(id);
                }
              }}
            >
              <ListItemIcon>
                <DeleteIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText sx={{ color: 'error.main' }}>Delete</ListItemText>
            </MenuItem>
          )}
        </Menu>
      </>
    );
  };

  const columns: GridColDef[] = [
    {
      field: 'sno',
      headerName: 'S.No',
      flex: 0.3,
    },
    {
      field: 'couponName',
      headerName: 'Offer Name',
      flex: 2,
    },
    {
      field: 'couponCode',
      headerName: 'Coupon Code',
      flex: 1.5,
    },
    {
      field: 'couponType',
      headerName: 'Type',
      flex: 1,
      valueGetter: (_value, row) => {
        return row.couponType || '-';
      },
    },
    {
      field: 'couponValue',
      headerName: 'Value',
      flex: 1,
      valueGetter: (_value, row) => {
        if (row.couponType === 'Percentage') {
          return `${row.couponValue}%`;
        }
        return `${row.couponValue} JOD`;
      },
    },
    {
      field: 'expires',
      headerName: 'Expires',
      flex: 1.5,
      valueGetter: (_value, row) => {
        if (row.couponStartDate && row.validityDays) {
          const startDate = new Date(row.couponStartDate);
          const endDate = new Date(startDate);
          endDate.setDate(endDate.getDate() + row.validityDays);
          return endDate.toLocaleDateString();
        }
        return '-';
      },
    },
    // Add Status column if user has Block or Unblock permission
    ...(hasPermission('promotionalAndOffers', 'Block') || hasPermission('promotionalAndOffers', 'Unblock')
      ? getActionColumns({
          module: 'promotionalAndOffers',
          onBlock: handleBlockClickButton,
          onUnblock: handleUnBlockClickButton,
        }).filter(col => col.field === 'status' || col.headerName === 'Status')
      : []),
    // Add Actions column combining Edit and Delete
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 0.8,
      filterable: false,
      disableColumnMenu: true,
      sortable: false,
      renderCell: (params) => <RowActions row={params.row} />,
    },
  ];

  // Transform offers data for DataGrid
  const rows = useMemo(() => {
    const offers = offersData?.data || [];
    return offers.map((offer: OfferDataType, index: number) => {
      const startDate = offer.couponStartDate ? new Date(offer.couponStartDate) : null;
      const endDate = startDate && offer.validityDays
        ? new Date(new Date(startDate).setDate(startDate.getDate() + offer.validityDays))
        : null;

      return {
        id: offer.offerId,
        sno: index + 1 + (Number(params.page - 1)) * Number(params.limit),
        couponName: offer.couponName,
        couponCode: offer.couponCode,
        couponType: offer.couponType,
        couponValue: offer.couponValue,
        maxCouponAmount: offer.maxCouponAmount,
        couponStartDate: offer.couponStartDate,
        validityDays: offer.validityDays,
        expires: endDate ? endDate.toISOString().split('T')[0] : '-',
        offerType: offer.offerType,
        termsAndConditions: offer.termsAndConditions,
        couponPerUserLimit: offer.couponPerUserLimit,
        totalCouponCount: offer.totalCouponCount,
        usedCouponCount: offer.usedCouponCount,
        // Spread offer properties (includes status and other fields)
        ...offer,
      };
    });
  }, [offersData, params.page, params.limit]);

  const handleRowSelection = (selectionModel: (string | number)[]) => {
    setSelectedRows(selectionModel as string[]);
  };

  // Calculate status of selected rows
  const selectedRowsStatus = useMemo(() => {
    if (selectedRows.length === 0) {
      return {
        hasSelection: false,
        allBlocked: false,
        allUnblocked: false,
        hasBlocked: false,
        hasUnblocked: false,
      };
    }

    const selectedOffers = rows.filter((row) => selectedRows.includes(row.id as string));
    // Treat undefined/null status as unblocked (default state)
    const blockedCount = selectedOffers.filter((offer) => offer.status === 'blocked').length;
    const unblockedCount = selectedOffers.filter((offer) => 
      offer.status === 'unblocked' || !offer.status || offer.status === ''
    ).length;

    return {
      hasSelection: true,
      allBlocked: blockedCount === selectedOffers.length && selectedOffers.length > 0,
      allUnblocked: unblockedCount === selectedOffers.length && selectedOffers.length > 0,
      hasBlocked: blockedCount > 0,
      hasUnblocked: unblockedCount > 0,
    };
  }, [selectedRows, rows]);

  const handlePageChange = (newPage: number) => {
    dispatch({ type: 'SET_PAGE', payload: newPage + 1 });
  };

  const handlePageSizeChange = (newSize: number) => {
    dispatch({ type: 'SET_LIMIT', payload: newSize });
  };



  return (
    <>
      <Typography variant="h5" fontWeight="bold" mb={2}>
        Manage Offers
      </Typography>
      <Box
        sx={{
          p: 2,
          border: '1px solid #e0e0e0',
          marginTop: '20px',
          color: '#666',
        }}
      >
        {snackbarOpen && (
          <Box sx={{ mb: 2 }}>
            <Alert
              severity={snackbarSeverity}
              onClose={() => setSnackbarOpen(false)}
            >
              {snackbarMessage}
            </Alert>
          </Box>
        )}

        <Box
          sx={{
            // p: 2,
            mb: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2, // Optional: adds spacing between wrapped items
          }}
        >
          {/* Search - Left */}
          <Box
            sx={{
              width: 600,
              border: '1px solid #ccc',
              borderRadius: '8px',
              background: '#fff',
            }}
          >
            <TextField
              fullWidth
              placeholder="Search by name, email, mobile"
              size="medium"
              value={searchTerm}
              onChange={handleSearchChange}
              sx={{
                '.MuiOutlinedInput-root': {
                  height: 40,
                  width: 600,
                  border: 'none', // remove inner border
                },
                '& fieldset': {
                  border: 'none', // remove Material-UI default border
                },
                '& .MuiInputBase-input': {
                  paddingTop: '10px', // adjust this value to move the placeholder lower
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ fontSize: 20, color: '#aaa', mt: '4px' }}
                    />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Action Buttons + Add Button - Right */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            {selectedRowsStatus.hasSelection && (
              <PermissionActionButtons
                module="promotionalAndOffers"
                onBlock={selectedRowsStatus.hasUnblocked ? handleBlockClick : undefined}
                onUnblock={selectedRowsStatus.hasBlocked ? handleUnBlockClick : undefined}
                onDelete={handleDeleteClick}
              />
            )}
            {hasPermission('promotionalAndOffers', 'Add') && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddSubAdmin}
                size="small"
                sx={{
                  textTransform: 'none',
                  borderRadius: 2,
                  bgcolor: COLORS.primary.main,
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: COLORS.primary.main,
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                  },
                }}
              >
                Add Offer
              </Button>
            )}
          </Box>
        </Box>

        <EditStoreOwnerModal
          open={openAddDialog}
          onClose={handleCloseAddDialog}
          title="Add Offer"
        >
          <EditOffers onCancel={handleCloseAddDialog} />
        </EditStoreOwnerModal>

        <EditStoreOwnerModal
          open={openEditDialog}
          onClose={handleCloseEditDialog}
          title={modalContent === 'add' ? 'Add Offer' : 'Edit Offer'}
        >
          <EditOffers onCancel={handleCloseEditDialog} />
        </EditStoreOwnerModal>

        <CommonDataGrid
          key={`datagrid-page-${params.page}-limit-${params.limit}`}
          rows={rows || []}
          columns={columns}
          pageSize={params.limit}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          checkboxSelection
          loading={isLoading}
          page={params.page - 1}
          rowCount={offersData?.pagination?.totalItems}
          onRowSelectionModelChange={handleRowSelection}
          selectionModel={selectedRows}
          components={{}}
        />
      </Box>
    </>
  );
};

export default ManageOffers;
